import { Component, OnInit, inject, runInInjectionContext, Injector, OnDestroy, AfterViewInit } from '@angular/core';

import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { Auth, onAuthStateChanged, RecaptchaVerifier } from '@angular/fire/auth';
import { onValue } from '@angular/fire/database';
import {
  IonApp,
  IonRouterOutlet,
  IonMenu,
  IonContent,
  IonList,
  IonItem,
  IonMenuToggle,
  IonIcon,
  IonLabel,
  IonBadge,
  IonSearchbar,
  IonSplitPane,
  IonButton,
  MenuController,
  Platform,
} from '@ionic/angular/standalone';
import { AppCheck, getToken } from '@angular/fire/app-check';
import { environment } from '../environments/environment';
import { HeaderComponent } from './components/header/header.component';
import { APPPAGES, BUILD_CODE, NAV_SECTIONS, NavSection, NavPage, YEAR, VERSION } from 'src/app/services/constants';
import { AuthService } from './services/auth.service';
import { DataService } from './services/data.service';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Capacitor } from '@capacitor/core';
import { Subscription } from 'rxjs';

declare var google: any;

export function lifecycleSidebarCatalogSource(options: {
  production: boolean;
  hostname: string;
}): 'local' | 'published' {
  const hostname = String(options.hostname || '')
    .trim()
    .toLowerCase();
  return !options.production && ['localhost', '127.0.0.1', '::1'].includes(hostname) ? 'local' : 'published';
}

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  standalone: true,
  imports: [
    IonApp,
    IonRouterOutlet,
    IonMenu,
    IonContent,
    IonList,
    IonItem,
    IonMenuToggle,
    IonIcon,
    IonLabel,
    IonBadge,
    IonSearchbar,
    IonSplitPane,
    IonButton,
    RouterLink,
    RouterLinkActive,
    HeaderComponent,
  ],
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  public injector = inject(Injector);
  public angularAuth: Auth = inject(Auth);
  private router: Router = inject(Router);
  private authService: AuthService = inject(AuthService);
  private dataService: DataService = inject(DataService);
  private appCheck: AppCheck = inject(AppCheck);
  private menuCtrl: MenuController = inject(MenuController);
  private platform: Platform = inject(Platform);

  private recaptchaVerifier: RecaptchaVerifier | null = null;
  private recaptchaInitialized = false;

  public appPages = APPPAGES; // Legacy flat list for backward compat
  public navSections: NavSection[] = NAV_SECTIONS;
  public expandedSections: Set<string> = new Set();
  public navSearchText = '';
  public navCountBadges: Record<string, number> = {};
  public lastLoginDate: any;
  public userData: any = null;
  ionVersionNumber: any;
  isLoggedIn = false;
  userRole: any;
  selectedIndex = 0;
  year: any = YEAR;
  isDesktop = false;
  desktopSidebarOpen = false;
  version: any = VERSION;
  buildCode: any = BUILD_CODE;
  private authResolved = false;
  private authUnsub?: () => void;
  private routerSub?: Subscription;
  private navCountsUnsub?: () => void;
  private activeRouteSectionName: string | null = null;
  private readonly resizeHandler = () => this.checkDesktop();

  ngOnInit() {
    this.loadGoogleMaps();
    // Small delay to ensure the script has initialized the global google object
    setTimeout(() => this.bridgeAppCheckToMaps(), 500);
    runInInjectionContext(this.injector, () => {
      this.authUnsub = onAuthStateChanged(this.angularAuth, (user) => {
        if (!this.authResolved) {
          this.authResolved = true;
          if (!user) return; // ignore first null
        }
        if (!user) {
          this.isLoggedIn = false;
          this.userData = null;
          this.userRole = null;
          this.stopNavCountListener();
          this.navCountBadges = {};
          this.menuCtrl.close();
          return;
        }
        this.userData = user;
        user.getIdTokenResult().then((idTokenResult) => {
          const role = this.authService.getPrimaryRoleFromClaims(idTokenResult?.claims as Record<string, unknown>);
          if (role) {
            this.userRole = role;
            this.isLoggedIn = true;
            this.listenForNavCounts();
            this.menuCtrl.close();
          }
        });
      });
    });
    this.activeRouteSectionName = this.getActiveSectionName();
    this.routerSub = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.syncActiveNavigationSection();
        if (!this.isDesktop) this.menuCtrl.close();
      }
    });
    this.initialize();
    this.initializeDarkMode();
  }

  ngAfterViewInit() {
    // Initialize reCAPTCHA for MFA after view is ready
    this.initAuthRecaptchaVerifier();
  }

  /**
   * Initialize reCAPTCHA verifier for MFA phone verification
   * Must run after view init to ensure container exists
   */
  private async initAuthRecaptchaVerifier() {
    if (this.recaptchaInitialized) return;

    try {
      const el = document.getElementById('recaptcha-container');
      if (!el) {
        console.warn('reCAPTCHA container not found');
        return;
      }

      // ✅ Correct order: (auth, containerId, params)
      this.recaptchaVerifier = new RecaptchaVerifier(this.angularAuth, 'recaptcha-container', { size: 'invisible' });

      // Render once (recommended)
      await this.recaptchaVerifier.render();

      this.authService.setRecaptchaVerifier(this.recaptchaVerifier);
      this.recaptchaInitialized = true;
    } catch (err) {
      console.error('Auth reCAPTCHA init failed:', err);
      // Non-fatal: MFA will fail later with a clearer error
    }
  }

  initialize() {
    this.checkDesktop();
    window.addEventListener('resize', this.resizeHandler);
    this.platform.ready().then(async () => {
      if (Capacitor.isNativePlatform()) {
        // Apply initial mode
        this.applyStatusBarStyle();

        // Listen for OS appearance changes (iOS + Android 13+)
        window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
          this.applyStatusBarStyle();
        });

        await SplashScreen.hide();
      }
    });
  }

  async applyStatusBarStyle() {
    const isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;

    if (isDark) {
      // DARK MODE → light icons
      await StatusBar.setStyle({ style: Style.Light });
      await StatusBar.setBackgroundColor({ color: '#000000' });
    } else {
      // LIGHT MODE → dark icons
      await StatusBar.setStyle({ style: Style.Dark });
      await StatusBar.setBackgroundColor({ color: '#ffffff' });
    }

    // Normal mode → content below status bar
    await StatusBar.setOverlaysWebView({ overlay: false });

    await StatusBar.show();
  }

  private loadGoogleMaps() {
    // Create a <script> element dynamically
    const script = document.createElement('script');
    script.type = 'text/javascript';

    // Your full Google loader code but with dynamic key
    script.text = `
      (g => {
        var h,a,k,p="The Google Maps JavaScript API",c="google",l="importLibrary",q="__ib__",m=document,b=window;
        b=b[c]||(b[c]={});
        var d=b.maps||(b.maps={}),r=new Set,e=new URLSearchParams,
        u=()=>h||(h=new Promise(async(f,n)=>{
          await (a=m.createElement("script"));
          e.set("libraries",[...r]+"");
          for(k in g)e.set(k.replace(/[A-Z]/g,t=>"_"+t[0].toLowerCase()),g[k]);
          e.set("callback",c+".maps."+q);
          a.src=\`https://maps.\${c}apis.com/maps/api/js?\`+e;
          d[q]=f;
          a.onerror=()=>h=n(Error(p+" could not load."));
          a.nonce=m.querySelector("script[nonce]")?.nonce||"";
          m.head.append(a)
        }));
        d[l]?console.warn(p+" only loads once. Ignoring:",g)
             :d[l]=(f,...n)=>r.add(f)&&u().then(()=>d[l](f,...n))
      })({
        v: "weekly",
        key: "${environment.googleMapApiKey}"
      });
    `;

    document.head.appendChild(script);
  }

  private async bridgeAppCheckToMaps() {
    if (!environment.useAppCheck) return;

    try {
      const { Settings } = await (google.maps as any).importLibrary('core');

      Settings.getInstance().fetchAppCheckToken = async () => {
        const result = await getToken(this.appCheck);

        // We return ONLY the token to bypass the "unknown property" validator
        // Google Maps will fetch a new one for the next session if needed
        return {
          token: result.token,
        };
      };
    } catch (error) {
      console.error('App Check Bridge: Failed to initialize', error);
    }
  }

  private initializeDarkMode() {
    const systemDark = window.matchMedia('(prefers-color-scheme: dark)');

    // 1️⃣ Apply stored preference if available
    const saved = localStorage.getItem('darkmode');
    if (saved === 'true') {
      this.setDarkMode(true);
    } else if (saved === 'false') {
      this.setDarkMode(false);
    } else {
      // No saved preference → follow system
      this.setDarkMode(systemDark.matches);
    }

    // 2️⃣ Watch for system theme changes
    systemDark.addEventListener('change', (e) => {
      // Only follow system if user hasn’t manually chosen
      const saved = localStorage.getItem('darkmode');
      if (saved === null) this.setDarkMode(e.matches);
    });
  }

  private setDarkMode(isDark: boolean) {
    document.documentElement.classList.toggle('ion-palette-dark', isDark);
  }

  /**
   * Check if user can access a navigation section
   * Administrators see all sections
   */
  canAccessSection(section: NavSection): boolean {
    if (!this.userRole) return false;
    if (this.userRole === 'Administrator') return true;
    return section.roles.includes(this.userRole);
  }

  get filteredNavSections(): NavSection[] {
    const query = this.navSearchText.trim().toLowerCase();

    return this.navSections
      .filter((section) => this.canAccessSection(section))
      .map((section) => {
        const accessiblePages = section.pages.filter((page) => this.canAccessPage(page));

        if (!query) {
          return {
            ...section,
            pages: accessiblePages,
          };
        }

        const sectionMatches = section.section.toLowerCase().includes(query);
        const matchingPages = sectionMatches
          ? accessiblePages
          : accessiblePages.filter((page) => {
              const searchable = [page.title, page.url, page.badge, section.section]
                .filter(Boolean)
                .join(' ')
                .toLowerCase();
              return searchable.includes(query);
            });

        return {
          ...section,
          pages: matchingPages,
        };
      })
      .filter((section) => section.pages.length > 0);
  }

  onNavSearch(event: CustomEvent): void {
    this.navSearchText = String(event.detail?.value || '');
  }

  /**
   * Check if user can access a specific page
   * Administrators see all pages
   */
  canAccessPage(page: NavPage): boolean {
    if (!this.userRole) return false;
    if (this.userRole === 'Administrator') return true;
    return page.roles.includes(this.userRole);
  }

  /**
   * Toggle section expansion in accordion
   */
  toggleSection(sectionName: string): void {
    if (this.expandedSections.has(sectionName)) {
      this.expandedSections.delete(sectionName);
    } else {
      this.expandedSections.add(sectionName);
    }
  }

  /**
   * Check whether the current route belongs to a navigation section.
   * Prefix matching keeps the parent page active on nested detail routes.
   */
  isSectionActive(section: NavSection): boolean {
    return section.pages.some((page) => !page.isExternal && this.isPageActive(page));
  }

  private isPageActive(page: NavPage): boolean {
    const currentUrl = String(this.router.url || '')
      .split(/[?#]/, 1)[0]
      .replace(/\/$/, '');
    const pageUrl = String(page.url || '').replace(/\/$/, '');

    return !!pageUrl && (currentUrl === pageUrl || currentUrl.startsWith(`${pageUrl}/`));
  }

  sidebarQueryParams(page: NavPage): Record<string, string> | undefined {
    if (page.url !== '/lifecycle-coverage') return undefined;
    return {
      catalog: lifecycleSidebarCatalogSource({
        production: environment.production,
        hostname: globalThis.location?.hostname || '',
      }),
    };
  }

  private getActiveSectionName(): string | null {
    return this.navSections.find((section) => this.isSectionActive(section))?.section || null;
  }

  private syncActiveNavigationSection(): void {
    const nextSectionName = this.getActiveSectionName();

    if (this.activeRouteSectionName && this.activeRouteSectionName !== nextSectionName) {
      this.expandedSections.delete(this.activeRouteSectionName);
    }

    this.activeRouteSectionName = nextSectionName;
  }

  /**
   * Check if section is currently expanded
   */
  isSectionExpanded(sectionName: string): boolean {
    const section = this.navSections.find((item) => item.section === sectionName);

    return (
      this.navSearchText.trim().length > 0 ||
      this.expandedSections.has(sectionName) ||
      (section ? this.isSectionActive(section) : false)
    );
  }

  /**
   * Get badge color based on badge emoji
   * @param badge - Badge emoji (✅, 🚧, 🔥)
   * @returns Ionic color name
   */
  getBadgeColor(badge: string): string {
    if (badge === '✅') return 'success';
    if (badge === '🚧') return 'warning';
    if (badge === '🔥') return 'danger';
    return 'medium';
  }

  getNavCountBadge(key?: string): number | null {
    if (!key) return null;
    const value = this.navCountBadges[key];
    return Number.isFinite(value) && value > 0 ? value : null;
  }

  private listenForNavCounts() {
    if (this.navCountsUnsub) return;

    runInInjectionContext(this.injector, () => {
      this.navCountsUnsub = onValue(
        this.dataService.getAdminNavCounts(),
        (snapshot) => {
          const rawCounts = snapshot.exists() ? snapshot.val() || {} : {};
          this.navCountBadges = Object.entries(rawCounts).reduce<Record<string, number>>((counts, [key, value]) => {
            const count = Number(value || 0);
            counts[key] = Number.isFinite(count) ? Math.max(0, count) : 0;
            return counts;
          }, {});
        },
        (error) => {
          console.error('Failed to load admin nav counts', error);
          this.navCountBadges = {};
        }
      );
    });
  }

  private stopNavCountListener() {
    if (!this.navCountsUnsub) return;
    this.navCountsUnsub();
    this.navCountsUnsub = undefined;
  }

  async toggleNavigation() {
    if (!this.isDesktop) {
      await this.menuCtrl.toggle();
      return;
    }

    this.desktopSidebarOpen = !this.desktopSidebarOpen;
    if (!this.desktopSidebarOpen) {
      window.setTimeout(() => this.menuCtrl.close());
    }
  }

  private checkDesktop() {
    const isDesktop = window.innerWidth >= 992;
    if (isDesktop === this.isDesktop) return;

    this.isDesktop = isDesktop;
    this.desktopSidebarOpen = false;
    this.menuCtrl.close();
  }

  ngOnDestroy() {
    this.authUnsub?.();
    this.routerSub?.unsubscribe();
    this.stopNavCountListener();
    window.removeEventListener('resize', this.resizeHandler);
  }
}
